import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopAppBar from '../../components/feature/TopAppBar';
import Button from '../../components/base/Button';
import DeliverySteps from './components/DeliverySteps';
import DeliveryTimer from './components/DeliveryTimer';
import QuickContactBar from './components/QuickContactBar';

type DeliveryStatus = 'heading_to_pickup' | 'at_pickup' | 'picked_up' | 'heading_to_customer' | 'at_customer';

export default function ActiveDelivery() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<DeliveryStatus>('heading_to_pickup');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [startTime] = useState(Date.now());
  
  const jobDetails = {
    orderId: 'DOM2847',
    branchName: 'Domino\'s Stockholm City',
    pickupAddress: 'Drottninggatan 89, 111 60 Stockholm',
    pickupPhone: '+46 8 123 456',
    deliveryAddress: 'Sveavägen 45, 3tr, 113 59 Stockholm',
    customerName: 'Erik Andersson',
    customerPhone: '+46 70 123 4567',
    distance: '2.4 km',
    estimatedTime: 9,
    payout: '89',
  };
  
  const handleNextStep = () => {
    if (status === 'heading_to_pickup') {
      setStatus('at_pickup');
    } else if (status === 'at_pickup') {
      setStatus('picked_up');
      setTimeout(() => setStatus('heading_to_customer'), 100);
    } else if (status === 'heading_to_customer') {
      setStatus('at_customer');
    } else if (status === 'at_customer') {
      setShowCompleteModal(true);
    }
  };
  
  const handleComplete = () => {
    setShowCompleteModal(false);
    navigate('/home');
  };
  
  const getButtonText = () => {
    switch (status) {
      case 'heading_to_pickup':
        return t('arrivedAtPickup') || 'Arrived at Pickup';
      case 'at_pickup':
        return t('confirmPickup') || 'Confirm Pickup';
      case 'picked_up':
      case 'heading_to_customer':
        return t('arrivedAtCustomer') || 'Arrived at Customer';
      case 'at_customer':
        return t('completeDelivery') || 'Complete Delivery';
      default:
        return 'Next';
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <TopAppBar 
        title={t('activeDelivery') || 'Active Delivery'}
        showBack={false}
      />
      
      <div className="pt-14 pb-32">
        {/* Map Section */}
        <div className="relative h-80 bg-gray-200">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2034.8!2d18.0686!3d59.3293!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTnCsDE5JzQ1LjUiTiAxOMKwMDQnMDcuMCJF!5e0!3m2!1sen!2sse!4v1234567890"
            className="w-full h-full"
            style={{ border: 0 }}
            loading="lazy"
          ></iframe>
          
          {/* Floating Info Card */}
          <div className="absolute top-4 left-4 right-4">
            <div className="bg-white rounded-2xl p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-mono font-bold text-gray-500">ORDER #{jobDetails.orderId}</p>
                  <p className="text-sm font-bold text-gray-900">{jobDetails.branchName}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#E31837]">{jobDetails.payout} SEK</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-map-pin-line"></i>
                <span>{jobDetails.distance}</span>
                <span>•</span>
                <i className="ri-time-line"></i>
                <span>{jobDetails.estimatedTime} min</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Timer Section */}
        <div className="px-4 -mt-6 mb-4">
          <DeliveryTimer startTime={startTime} estimatedMinutes={jobDetails.estimatedTime} />
        </div>
        
        {/* Steps Section */}
        <div className="px-4 mb-4">
          <DeliverySteps 
            status={status}
            pickupAddress={jobDetails.pickupAddress}
            deliveryAddress={jobDetails.deliveryAddress}
            customerName={jobDetails.customerName}
          />
        </div>
        
        {/* Quick Contact Bar */}
        <div className="px-4 mb-4">
          <QuickContactBar
            storePhone={jobDetails.pickupPhone}
            customerPhone={jobDetails.customerPhone}
            pickupAddress={jobDetails.pickupAddress}
            deliveryAddress={jobDetails.deliveryAddress}
            status={status}
          />
        </div>
        
        {/* Order Details Quick View */}
        <div className="px-4">
          <button
            onClick={() => navigate(`/job/${jobDetails.orderId}`)}
            className="w-full bg-white rounded-2xl p-4 shadow-md flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-file-list-3-line text-[#006491] text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-900">{t('orderDetails') || 'Order Details'}</p>
                <p className="text-xs text-gray-600">{t('viewFullDetails') || 'View full details'}</p>
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
          </button>
        </div>
      </div>
      
      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <Button
          onClick={handleNextStep}
          variant="primary"
          size="large"
          fullWidth
          icon={<i className="ri-check-line"></i>}
        >
          {getButtonText()}
        </Button>
        
        {status === 'heading_to_pickup' && (
          <button
            onClick={() => navigate('/home')}
            className="w-full mt-3 text-sm text-gray-600 py-2"
          >
            {t('cancelDelivery') || 'Cancel Delivery'}
          </button>
        )}
      </div>
      
      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-3xl w-full p-6 animate-slide-up">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6"></div>
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('deliveryCompleted') || 'Delivery Completed!'}</h3>
              <p className="text-base text-gray-600 mb-4">
                {t('greatJob') || 'Great job! You earned'}
              </p>
              <p className="text-4xl font-bold text-[#E31837] mb-2">{jobDetails.payout} SEK</p>
              <p className="text-sm text-gray-500">
                {t('addedToEarnings') || 'Added to your earnings'}
              </p>
            </div>
            <div className="space-y-3">
              <Button onClick={handleComplete} variant="primary" size="large" fullWidth>
                {t('backToHome') || 'Back to Home'}
              </Button>
              <Button onClick={() => navigate('/jobs')} variant="secondary" size="large" fullWidth>
                {t('findNextJob') || 'Find Next Job'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
